import { match, Default as _ } from '@nike/rcf-fp';

import {
  TRUE_APP, TOKEN_V2, NPOS_APP, TOKEN_V1
} from './constants';
import {
  Messages, getTargetOrigin, isRetailLogin, isValidTokenOrigin, getApplicationSource, getTokenVersion
} from './iframeMessaging';
import { getCountryByStoreId } from './service-calls';

const { countryToGeoMap } = require('./static/nike-geo-mappings');

export const decodeTokenPayload = (token) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const binaryString = window.atob(base64);
  const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
};

const filterByRegion = (countries, nikeGeo) => countries.filter((country) => (countryToGeoMap[country.value] === nikeGeo));

const filterByCountry = (countries, countryCode) => countries.filter((country) => (country.value === countryCode));

export default class Auth {
  static getAuth = () => localStorage.getItem('accessToken');

  static getAuthCountry = () => decodeTokenPayload(this.getAuth()).nike_country;

  static getAuthGroups = () => {
    if (isRetailLogin()) {
      return new Set(['retail']);
    } else {
      return new Set(decodeTokenPayload(this.getAuth()).groups);
    }
  }

  static getCountriesByAuthGroup = (countries) => {
    const groups = this.getAuthGroups();

    const isGlobalUser = () => groups.has('App.ElectronicJournal.GLOBAL');
    const isAPLAUser = () => groups.has('App.ElectronicJournal.GEO.APLA');
    const isEMEAUser = () => groups.has('App.ElectronicJournal.GEO.EMEA');
    const isGCUser = () => groups.has('App.ElectronicJournal.GEO.GC');
    const isNAUser = () => groups.has('App.ElectronicJournal.GEO.NA');
    const isRetailUser = () => groups.has('retail');

    return match()( // Will a user ever not be GLOBAL, but has access to more than two regions?
      [isGlobalUser, countries],
      [isAPLAUser, filterByRegion(countries, 'ASIA_PACIFIC_LATIN_AMERICA')],
      [isEMEAUser, filterByRegion(countries, 'EUROPE_MIDDLE_EAST_AFRICA')],
      [isGCUser, filterByRegion(countries, 'GREATER_CHINA')],
      [isNAUser, filterByRegion(countries, 'NORTH_AMERICA')],
      [isRetailUser, filterByCountry(countries, this.getCountry())],
      [_, []],
    );
  };

  static getCountriesForRetailLogin = async (countries) => {
    const groups = this.getAuthGroups();
    const isRetailUser = () => groups.has('retail');
    return isRetailUser ? filterByCountry(countries, await this.getCountry()) : [];
  };

  static getCountry = async () => ((getTokenVersion() === TOKEN_V2 && getApplicationSource() === NPOS_APP) ? getCountryByStoreId(this.getStoreId()) : decodeTokenPayload(this.getAuth()).nike_country);

  static getStoreId = () => decodeTokenPayload(this.getAuth()).nike_storeId;

  static getVersionFromToken = () => {
    const tokenObj = decodeTokenPayload(this.getAuth());
    return tokenObj.nike_country && tokenObj.nike_store ? TOKEN_V1 : TOKEN_V2;
  }

  // * Dev team can access historical data (transactions older than 13 months) for all stores
  static hasHistoricalDateAccess = () => {
    const groups = this.getAuthGroups();
    return groups.has('App.ElectronicJournal.Historical.Data');
  }

  // * A Store Athlete act as Admin for a perticular store.
  static isAdmin = () => (isRetailLogin() ? true : this.getAuthGroups().has('App.ElectronicJournal.ADMIN'));

  // * Function isValidTokenOrigin is linked to localStorage.clear() in index.js
  static isAuthenticated = () => (isValidTokenOrigin() && (new Date().getTime() < (JSON.parse(localStorage.getItem('expiresAt') * 1000))));

  static isTokenRequestComplete = (ejAppSource, authObj) => {
    const isNPOS = ejAppSource === NPOS_APP;
    const isTRUE = ejAppSource === TRUE_APP;
    return isNPOS || (isTRUE && authObj?.authType && authObj?.token);
  };

// * Logout to be used when running on TRUE platform
  static sourceAppLogout = () => {
    const applicationSource = getApplicationSource();
    localStorage.clear();
    sessionStorage.clear();

    if (applicationSource === TRUE_APP) {
      window.parent.postMessage({ name: Messages.SetTitle, title: 'T.R.U.E' }, getTargetOrigin());
      window.parent.postMessage({ name: Messages.Close }, getTargetOrigin());
    }
  }
}
